<script setup>
defineOptions({ name: "SegmentModaPage" });
import { onMounted, onUnmounted, ref, defineAsyncComponent } from "vue";
import {
  CreditCardIcon,
  Square3Stack3DIcon,
  MegaphoneIcon,
  ChartBarIcon,
  DocumentCheckIcon,
  ScaleIcon,
} from "@heroicons/vue/24/outline";
import {
  ShoppingBagIcon,
  TagIcon,
  GiftIcon,
  SparklesIcon,
  SwatchIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import imageBg from "@/assets/segments/moda.webp";
import visaoGeralImg from "@/assets/visaogeralmoda.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));
const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// ---- Navegação fixa ----
const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Desafios", href: "#desafios" },
  { label: "Categorias", href: "#categorias" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const openConsultorModal = () => {
  window.dispatchEvent(new CustomEvent("open-consultor-modal"));
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, { passive: true });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

// Desafios reais de quem toca uma loja de moda, roupas e calçados.
const desafios = [
  {
    icon: SwatchIcon,
    titulo: "Venda por grade (cor e tamanho)",
    problema:
      "Cada peça vira várias variações de cor e tamanho — sistema genérico mistura tudo e a conferência de estoque vira um parto.",
    solucao:
      "Cadastro de coleções inteiras com grade de cor e tamanho, cada variação com saldo, preço e código de barras próprios.",
  },
  {
    icon: ScaleIcon,
    titulo: "Troca por tamanho errado",
    problema:
      "O cliente compra, troca depois — se o estoque não separa a grade direito, a troca bagunça o saldo de outras variações.",
    solucao:
      "Como cada grade tem controle próprio, a troca entra e sai do saldo certo, sem misturar cor e tamanho.",
  },
  {
    icon: ChartBarIcon,
    titulo: "Fim de coleção e produto parado",
    problema:
      "O que não vende na estação vira prejuízo se ninguém perceber a tempo de fazer liquidação.",
    solucao:
      "Relatórios por categoria e coleção mostram o que está girando e o que está parado, pra planejar a liquidação antes de virar prejuízo.",
  },
  {
    icon: ShoppingBagIcon,
    titulo: "Venda em vários canais",
    problema:
      "Vender o mesmo produto em canais diferentes com estoques separados gera ruptura ou venda duplicada.",
    solucao:
      "Estoque único entre loja física, e-commerce e marketplaces — o que vende em um canal já sai do mesmo saldo.",
  },
  {
    icon: DocumentCheckIcon,
    titulo: "Emissão fiscal na correria do caixa",
    problema:
      "Emitir nota fiscal separada da venda atrasa o atendimento e é onde mais acontece erro no fim do dia.",
    solucao:
      "Emissão de NF-e, NFC-e e NFS-e integrada à venda, sem digitar item de novo nem depender de sistema paralelo.",
  },
  {
    icon: TagIcon,
    titulo: "Etiquetagem de coleção nova",
    problema:
      "Coleção nova chegando significa etiquetar e conferir centenas de peças — na mão, é lento e sujeito a erro de preço.",
    solucao:
      "Etiquetas com código de barras geradas pelo próprio sistema, agilizando recebimento e conferência no caixa.",
  },
];

// Capacidades reais do sistema — sem número inventado, só o que o Kali de fato faz.
const capacidades = [
  {
    icon: SwatchIcon,
    titulo: "Grade de cor e tamanho",
    texto: "Cada variação com saldo, preço e código de barras próprios.",
  },
  {
    icon: TagIcon,
    titulo: "Etiqueta automática",
    texto: "Coleção nova chega e sai etiquetada, sem conferência manual de preço.",
  },
  {
    icon: ShoppingBagIcon,
    titulo: "Estoque único multicanal",
    texto: "Loja física, e-commerce e marketplace vendem do mesmo saldo.",
  },
];

// Categorias de produto de uma loja de moda.
const categorias = [
  { icon: ShoppingBagIcon, label: "Roupas femininas" },
  { icon: TagIcon, label: "Roupas masculinas" },
  { icon: Square3Stack3DIcon, label: "Calçados" },
  { icon: GiftIcon, label: "Moda infantil" },
  { icon: SparklesIcon, label: "Acessórios" },
  { icon: SwatchIcon, label: "Moda íntima" },
];

const funcionalidades = [
  {
    icon: CreditCardIcon,
    eyebrow: "Frente de caixa",
    titulo: "PDV rápido com TEF e PIX",
    texto: "Receba em qualquer forma de pagamento com fechamento de caixa diário simplificado.",
  },
  {
    icon: Square3Stack3DIcon,
    eyebrow: "Estoque",
    titulo: "Grade por cor e tamanho",
    texto: "Cadastre coleções inteiras e acompanhe cada variação separadamente, sem confundir saldo.",
  },
  {
    icon: MegaphoneIcon,
    eyebrow: "Promoções",
    titulo: "Liquidação de fim de coleção",
    texto: "Programe campanhas e liquidações com controle do impacto sobre margem e ticket médio.",
  },
  {
    icon: ChartBarIcon,
    eyebrow: "Gestão",
    titulo: "Relatórios por categoria",
    texto: "Veja o que vende mais, o que precisa de reposição e o que está parado, por loja ou rede.",
  },
];

const documentosFiscais = ["NF-e", "NFC-e", "NFS-e", "CT-e", "CT-e OS", "MDF-e"];

const controleTributario = [
  { label: "Sped Fiscal", shade: "bg-kali-blue-700" },
  { label: "Bloco H", shade: "bg-kali-blue-600" },
  { label: "Bloco K", shade: "bg-kali-blue-500" },
  { label: "EFD Contribuições", shade: "bg-kali-blue-400" },
];

useSeo({
  title: "Sistema para Loja de Moda, Roupas e Calçados | Kali Gestão",
  description:
    "Grade de cor e tamanho, troca, fim de coleção e venda multicanal — o Kali Gestão organiza a rotina da sua loja de moda.",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Gestão para Moda",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/segmento/moda",
    description:
      "Sistema de gestão para lojas de moda, roupas e calçados: grade de cor e tamanho, troca, liquidação e venda multicanal.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SegmentHero
      title="Mais controle sobre coleções, grades e vendas da sua loja de moda"
      description="Grade de cor e tamanho, troca, fim de coleção e venda em vários canais — o Kali Gestão organiza tudo isso em um só sistema."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=moda"
      secondary-label="Ver outros segmentos"
      secondary-href="/segmentos"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    />

    <!-- Navegação fixa -->
    <section class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <nav class="flex overflow-x-auto relative gap-6 items-center h-16" aria-label="Navegação da página">
          <a
            v-for="item in navLinks"
            :key="item.href"
            :href="item.href"
            class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
            :class="{ 'text-[#006086] active': activeNav === item.href }"
            @click="(e) => handleNavClick(e, item.href)"
          >
            {{ item.label }}
          </a>
        </nav>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Gestão"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito pra rotina de quem vende moda
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Lojas de roupas, calçados e acessórios lidam com um detalhe que
            sistemas genéricos não resolvem bem: a grade. O Kali Gestão
            organiza cada cor, tamanho e modelo separadamente, do cadastro da
            coleção até o relatório de vendas — em loja física, online ou
            marketplace, 100% online.
          </p>
          <div class="grid gap-4 mt-10 sm:grid-cols-3 lg:max-w-none">
            <div
              v-for="cap in capacidades"
              :key="cap.titulo"
              v-reveal
              class="p-4 bg-[#e6f0f4] rounded-2xl"
            >
              <component :is="cap.icon" class="w-6 h-6 text-kali-blue-600" />
              <p class="mt-2 text-sm font-bold text-gray-900">{{ cap.titulo }}</p>
              <p class="mt-1 text-xs leading-relaxed text-gray-600">{{ cap.texto }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Desafios reais do segmento -->
    <section
      id="desafios"
      class="py-16 bg-white scroll-mt-24"
      style="background-image: repeating-linear-gradient(45deg, rgba(0,96,134,0.06) 0px, rgba(0,96,134,0.06) 1px, transparent 1px, transparent 12px);"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <span
            class="inline-flex rounded-full bg-[#e6f0f4] px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-kali-blue-700"
            >Rotina real da loja</span
          >
          <h2
            class="mt-4 text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Seis dores que toda loja de moda conhece
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Não é genérico: é grade de cor e tamanho, é troca, é fim de
            coleção. Veja como o Kali Gestão resolve cada uma.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <article
            v-for="d in desafios"
            :key="d.titulo"
            v-reveal
            class="p-7 bg-white rounded-3xl border border-white transition shadow-[0_20px_45px_-15px_rgba(0,50,58,0.35)] hover:-translate-y-1 hover:shadow-[0_28px_55px_-15px_rgba(0,50,58,0.42)]"
          >
            <component :is="d.icon" class="w-10 h-10 text-kali-blue-600" />
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ d.titulo }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ d.problema }}
            </p>
            <div class="p-4 mt-4 rounded-xl bg-[#e6f0f4]">
              <p class="text-sm leading-relaxed text-kali-blue-900">
                <span class="font-bold">Como o Kali resolve:</span>
                {{ d.solucao }}
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Categorias de produto -->
    <section id="categorias" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-3 gap-x-6 gap-y-10 sm:grid-cols-3">
          <div v-for="c in categorias" :key="c.label" v-reveal class="flex flex-col items-center text-center group">
            <component
              :is="c.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600">
              {{ c.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Uma coleção inteira, sem virar
            <span class="text-kali-blue-600">bagunça</span>
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Roupa feminina, masculina, infantil, calçado, acessório — cada
            categoria com sua própria grade. O Kali organiza tudo no mesmo
            cadastro, com etiquetas e relatórios pra você nunca perder o
            controle do que entra e sai.
          </p>
          <button
            type="button"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
            @click="openConsultorModal"
          >
            Fale com um consultor
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades em destaque -->
    <section id="funcionalidades" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Tudo que a sua loja precisa em um só sistema
          </h2>
        </div>
        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="f in funcionalidades" :key="f.titulo" v-reveal class="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <component :is="f.icon" class="w-9 h-9 text-kali-blue-600" />
            <p class="mt-4 text-xs font-bold tracking-wide text-kali-blue-600 uppercase">{{ f.eyebrow }}</p>
            <h3 class="mt-1 text-base font-bold text-gray-900">{{ f.titulo }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ f.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div v-reveal class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl">
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Gestão acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua empresa em conformidade sem parar de
              vender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold">
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg">
              <ScaleIcon class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emita documentos fiscais de forma descomplicada, vinculada
              automaticamente ao site da Receita Federal.
            </p>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-lg font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Controle fiscal e tributário
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Desenvolvido para MEI, ME, Simples, Lucro Real e Lucro
              Presumido, com emissão dos principais documentos fiscais.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="c in controleTributario"
                :key="c.label"
                class="flex items-end p-4 h-24 font-bold text-white rounded-lg"
                :class="c.shade"
              >
                {{ c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'O sistema controla estoque por grade (cor e tamanho)?',
            answer:
              'Sim. Cada variação de cor, tamanho e modelo é controlada separadamente, com posição de estoque, preço e código de barras próprios.',
          },
          {
            question: 'É possível integrar com loja virtual e marketplaces?',
            answer:
              'Sim. O Kali Gestão centraliza o estoque entre loja física, e-commerce e marketplaces, evitando vender o que não tem disponível.',
          },
          {
            question: 'Como funciona a troca de coleção e produtos parados?',
            answer:
              'Os relatórios por categoria e coleção mostram o que está girando e o que está parado, ajudando a planejar liquidações.',
          },
          {
            question: 'Consigo emitir NF-e, NFC-e e NFS-e direto do sistema?',
            answer:
              'Sim. A emissão fiscal está integrada à venda, sem necessidade de sistemas paralelos ou digitação manual de itens.',
          },
        ]"
        description="Dúvidas sobre o Kali Gestão pra loja de moda? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
